import React, { useMemo, useState } from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Box, Button, Checkbox, Grid, GridItem, HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IntegrationExternalIdStack from '../../../../../components/IntegrationExternalIdStack';
import { hideModal } from '../../../../../redux/modules/modal';
import { useMergeInsureds } from '../../../../../services/insured-service';
import colors from '../../../../../styles/theme/foundation/colors';
import { arrayFilterAndMap } from '../../../../../utils/arrayUtil';

import InfoLabel from './InfoLabel';

const MergeInsuredsModal = ({ insureds }) => {
  const dispatch = useDispatch();
  const { mutate: mergeInsureds, isLoading } = useMergeInsureds();
  const [mappedInsureds, setMappedInsureds] = useState(insureds.map((insured, index) => ({
    key: index,
    insuredId: insured.insuredId,
    insuredName: insured.insuredName,
    checked: false,
    insuredIntegrations: insured.insuredIntegrations,
    externalId: insured.insuredExternalId
  })));
  const [confirmationCheck, setConfirmationCheck] = useState(false);

  const handleCheckInsured = insuredId => {
    const newMappedInsureds = [...mappedInsureds];

    newMappedInsureds.map(insured => {
      insured.checked = insured.insuredId === insuredId;

      return insured;
    });

    setMappedInsureds(newMappedInsureds);
  };

  const handleConfirmationCheck = () => {
    setConfirmationCheck(!confirmationCheck);
  };

  const readyToSubmit = useMemo(() => {
    if (!confirmationCheck) {
      return false;
    }

    const selectedInsureds = mappedInsureds.filter(insured => insured.checked);

    return selectedInsureds.length > 0;
  }, [confirmationCheck, mappedInsureds]);

  const handleMergeInsureds = () => {
    const selectedInsured = mappedInsureds.find(insured => insured.checked);
    const insuredsToMerge = arrayFilterAndMap(
      mappedInsureds,
      insured => insured.insuredId !== selectedInsured?.insuredId,
      insured => (insured.insuredId)
    );

    mergeInsureds({
      insuredId: selectedInsured.insuredId,
      insuredsToMerge
    });

    dispatch(hideModal());
  };

  return (
    <Box>
      <Box
        bgColor={colors.greyScale['100']}
        borderRadius="md"
        boxShadow="md"
        mb={4}
        p={2}
      >
        <HStack justifyContent="flex-end" mb={2}>
          <Tooltip shouldWrapChildren label={<InfoLabel />}>
            <Icon as={BsInfoCircle} />
          </Tooltip>
        </HStack>

        {mappedInsureds.map(insured => (
          <Grid
            gap={2}
            key={insured.insuredId}
            my={4}
            templateColumns="repeat(10, 1fr)"
          >
            <GridItem colSpan={1}>
              <Checkbox
                borderColor={colors.greyScale['400']}
                isChecked={insured.checked}
                mt="5px"
                onChange={() => handleCheckInsured(insured.insuredId)}
              />
            </GridItem>
            <GridItem colSpan={5}>
              <Text color={colors.greyScale['900']} textAlign="left">{insured.insuredName}</Text>
              <Text color={colors.greyScale['700']} textAlign="left">{`Insured Id: ${insured.insuredId}`}</Text>
            </GridItem>
            <GridItem colSpan={4}>
              <IntegrationExternalIdStack
                customExternalId={insured?.externalId}
                integrations={insured.insuredIntegrations}
                resourceName="insuredId"
              />
            </GridItem>
          </Grid>
        ))}
      </Box>

      <Box mb={6}>
        <Text mb={4}>Merging insureds is permanent.  Are you sure you want to merge these insureds?</Text>
        <Checkbox
          isChecked={confirmationCheck}
          onChange={handleConfirmationCheck}
        >
          I understand this action is irreversible
        </Checkbox>
      </Box>

      <Button
        isDisabled={!readyToSubmit}
        isLoading={isLoading}
        onClick={handleMergeInsureds}
      >
        Merge Insureds
      </Button>
    </Box>
  );
};

MergeInsuredsModal.propTypes = {
  insureds: PropTypes.arrayOf(PropTypes.shape({
    insuredId: PropTypes.number,
    insuredName: PropTypes.string
  }))
};

export default MergeInsuredsModal;
